import { EmptyState, SectionCard } from '@atlas/ui-components'
import { Chip, Skeleton } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import { WaitTime } from '@/components/reviews/WaitTime'

/**
 * One item waiting on a reviewer. Shaped around what a queue shows rather than
 * around a work order, so other reviewable kinds can join it as more rows.
 */
export interface ReviewQueueRow {
	key: string
	href: string
	code: string
	title: string
	/** The configured label of the review stage the item waits on. */
	stageLabel: string
	waitHours: number
}

/**
 * "Waiting on your review": the longest-waiting items under review, with how
 * many more sit behind them, so the card and the hero counter always agree.
 */
export function ReviewQueueCard({
	rows,
	total,
	isLoading,
}: {
	rows: ReviewQueueRow[]
	/** Every item in the queue; `rows` may show only the first few. */
	total: number
	isLoading?: boolean
}) {
	const more = total - rows.length
	return (
		<SectionCard
			title="Waiting on your review"
			action={
				<Link to="/work-orders" className="text-xs font-medium text-ds-text hover:text-ds-info">
					Open queue →
				</Link>
			}
			className="p-0 px-6 py-5.5"
		>
			{isLoading ? (
				<div aria-busy="true" className="border-t border-ds-border pt-3">
					<Skeleton shape="text" className="w-2/3" />
					<span className="sr-only">Loading…</span>
				</div>
			) : rows.length === 0 ? (
				<EmptyState className="mt-3 border-t border-ds-border" title="Nothing waiting on you." />
			) : (
				<>
					{rows.map((row) => (
						<Link
							key={row.key}
							to={row.href}
							className="flex w-full items-center gap-3 border-t border-ds-border py-3 hover:bg-ds-surface"
						>
							<span className="font-mono text-ds-2xs text-ds-text-subtle">{row.code}</span>
							<span className="min-w-0 flex-1 truncate text-ds-sm text-ds-text">{row.title}</span>
							<Chip variant="muted" size="sm" className="max-w-40 flex-none" title={row.stageLabel}>
								{row.stageLabel}
							</Chip>
							<span className="flex w-14 flex-none justify-end">
								<WaitTime hours={row.waitHours} />
							</span>
						</Link>
					))}
					{more > 0 ? (
						<Link
							to="/work-orders"
							className="block border-t border-ds-border pt-3 text-xs text-ds-text-subtle hover:text-ds-text"
						>
							+{more} more waiting
						</Link>
					) : null}
				</>
			)}
		</SectionCard>
	)
}
